import { useEffect, useState } from "react";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import { useTranslation } from "react-i18next";
import { FaDownload, FaEye } from "react-icons/fa";
import "react-loading-skeleton/dist/skeleton.css";
import ActionMenu from "../../../component/ActionMenu";
import SkeletonLoading from "../../../component/SkeletonLoading";
import Tooltip from "../../../component/Tooltip";
import { formatDateToLocal } from "../../../utils/dateAndTimeConversion";
import "../../AddVehicle.scss";
import {
  useGetAllParentsQuery,
  useGetAllStudentsQuery,
} from "../../../store/api/school/PraentSlice";
import Select from "react-select";
import { useDebounce } from "use-debounce";
import AdminPagination from "../../../component/pagination/AdminPagination";
import { toast } from "react-toastify";

const AllStudents = () => {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebounce(search, 300);

  // ── Filters ──────────────────────────────────────────────────────────────
  const [filters, setFilters] = useState({
    search: "",
    status: "",
    parent_id: "",
    is_assigned: "",
    page: 1,
    limit: 10,
  });

  // ── Checkbox Selection ───────────────────────────────────────────────────
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  // ── Modal ─────────────────────────────────────────────────────────────────
  const [showModal, setShowModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);

  // ── API ───────────────────────────────────────────────────────────────────
  const { data, isLoading } = useGetAllStudentsQuery(filters);
  const students = data?.data?.data || [];
  const pagination = data?.data?.pagination || {};

  const { data: parentsData } = useGetAllParentsQuery({ limit: 1000 });
  const parentOptions = parentsData?.data?.parents || [];

  const getStatusBadge = (status) => {
    switch (status?.toLowerCase()) {
      case "active":
        return <span className="badge bg-success">Active</span>;
      case "pending":
        return <span className="badge bg-warning">Pending</span>;
      case "inactive":
        return <span className="badge bg-secondary">Inactive</span>;
      case "suspended":
        return <span className="badge bg-danger">Suspended</span>;
      default:
        return <span className="badge bg-info">N/A</span>;
    }
  };

  const getDisabilityBadge = (isDisabled) =>
    isDisabled === 1 ? (
      <span className="badge bg-danger">Yes</span>
    ) : (
      <span className="badge bg-success">No</span>
    );

  const getAssignedBadge = (isAssigned) =>
    isAssigned ? (
      <span className="badge bg-success">Assigned</span>
    ) : (
      <span className="badge bg-secondary">Not Assigned</span>
    );

  // Handle individual student selection
  const handleSelectStudent = (studentId) => {
    setSelectedStudents((prev) => {
      if (prev.includes(studentId)) {
        return prev.filter((id) => id !== studentId);
      } else {
        return [...prev, studentId];
      }
    });
  };

  const isSelectable = (student) =>
    student.status?.toLowerCase() !== "pending" &&
    !student.is_assigned_to_reservation;

  // Handle select all on current page
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedStudents([]);
    } else {
      const currentPageStudentIds = students
        .filter(isSelectable)
        .map((student) => student.id);
      setSelectedStudents(currentPageStudentIds);
    }
    setSelectAll(!selectAll);
  };

  // Export selected students as CSV
  const exportToCSV = () => {
    if (selectedStudents.length === 0) {
      toast.error(t("please_select_students_to_export"));
      return;
    }

    // Filter selected students from current page (exclude pending)
    const selectedStudentsData = students.filter(
      (student) =>
        selectedStudents.includes(student.id) &&
        student.status?.toLowerCase() !== "pending",
    );

    // Define CSV headers
    const headers = [
      "student_id",
      "Sr No",
      "Student Name",
      "Roll Number",
      "Parent Name",
      "Parent Email",
      "Parent Phone",
      "Status",
      "Disabled",
      "Reservation Status",
      "Address",
      "Created At",
      "Disability Notes",
      "Doctor Phone",
    ];

    // Prepare CSV data
    const csvData = selectedStudentsData.map((student, index) => ({
      student_id: student.id,
      "Sr No": index + 1,
      "Student Name": student.full_name || "N/A",
      "Roll Number": student.roll_number || "N/A",
      "Parent Name": student.parent_name || "N/A",
      "Parent Email": student.parent_email || "N/A",
      "Parent Phone": student.parent_phone || "N/A",
      Status: student.status || "N/A",
      Disabled: student.is_disabled === 1 ? "Yes" : "No",
      "Reservation Status": student.is_assigned_to_reservation
        ? "Assigned"
        : "Not Assigned",
      Address: student.street || "N/A",
      "Created At": student.created_at
        ? formatDateToLocal(student.created_at)
        : "N/A",
      "Disability Notes": student.disability_notes || "N/A",
      "Doctor Phone": student.dr_phone_no || "N/A",
    }));

    // Convert to CSV string
    const csvString = convertToCSV(csvData, headers);

    // Download file
    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `students_export_${new Date().toISOString().slice(0, 19)}.csv`,
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Helper function to convert JSON to CSV
  const convertToCSV = (data, headers) => {
    const csvRows = [];

    // Add headers
    csvRows.push(headers.join(","));

    // Add data rows
    for (const row of data) {
      const values = headers.map((header) => {
        const value = row[header] || "";
        // Escape quotes and wrap in quotes if contains comma or newline
        const escapedValue = String(value).replace(/"/g, '""');
        return `"${escapedValue}"`;
      });
      csvRows.push(values.join(","));
    }

    return csvRows.join("\n");
  };

  // Export all students (from all pages)
  const exportAllToCSV = async () => {
    // This would require an API call to get all students
    // For now, we'll export current page's selected or all students
    if (students.length === 0) {
      toast.error(t("no_students_to_export"));
      return;
    }

    const allStudentsData = students.filter(isSelectable);

    if (allStudentsData.length === 0) {
      toast.error(t("no_students_to_export"));
      return;
    }

    const headers = [
      "student_id",
      "Sr No",
      "Student Name",
      "Roll Number",
      "Parent Name",
      "Parent Email",
      "Parent Phone",
      "Status",
      "Disabled",
      "Reservation Status",
      "Address",
      "Created At",
      "Disability Notes",
      "Doctor Phone",
    ];

    const csvData = allStudentsData.map((student, index) => ({
      student_id: student.id,
      "Sr No": index + 1,
      "Student Name": student.full_name || "N/A",
      "Roll Number": student.roll_number || "N/A",
      "Parent Name": student.parent_name || "N/A",
      "Parent Email": student.parent_email || "N/A",
      "Parent Phone": student.parent_phone || "N/A",
      Status: student.status || "N/A",
      Disabled: student.is_disabled === 1 ? "Yes" : "No",
      "Reservation Status": student.is_assigned_to_reservation
        ? "Assigned"
        : "Not Assigned",
      Address: student.street || "N/A",
      "Created At": student.created_at
        ? formatDateToLocal(student.created_at)
        : "N/A",
      "Disability Notes": student.disability_notes || "N/A",
      "Doctor Phone": student.dr_phone_no || "N/A",
    }));

    const csvString = convertToCSV(csvData, headers);
    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `all_students_export_${new Date().toISOString().slice(0, 19)}.csv`,
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handlePageChange = (page) => {
    setFilters((prev) => ({ ...prev, page }));
  };

  const handleLimitChange = (limit) => {
    setFilters((prev) => ({
      ...prev,
      limit,
      page: 1,
    }));
  };

  const handleResetFilters = () => {
    setSearch("");
    setFilters({
      search: "",
      status: "",
      parent_id: "",
      is_assigned: "",
      page: 1,
      limit: 10,
    });
  };

  useEffect(() => {
    if (debouncedSearch) {
      setFilters((prev) => ({ ...prev, page: 1, search: debouncedSearch }));
    }
  }, [debouncedSearch]);

  useEffect(() => {
    if (!search.trim()) {
      setFilters((prev) => ({ ...prev, search: "" }));
    }
  }, [search]);

  return (
    <>
      <div className="d-flex flex-wrap align-items-center justify-content-between mb-3 gap-2">
        <div className="total">
          {t("total")} {t("students")}: {pagination?.total ?? 0}
        </div>

        <div className="d-flex flex-wrap gap-2 ms-auto">
          <div className="d-flex flex-grow-1">
            <input
              type="text"
              className="form-control"
              placeholder={`${t("search")} (${t("name")}, ${t("roll")}, ${t("parent")})...`}
              value={search}
              onChange={(e) => setSearch(e.target.value.trim())}
            />
          </div>
          <div className="d-flex flex-grow-1">
            {/* Status Filter */}
            <select
              className="form-select flex-grow-1"
              value={filters.status}
              onChange={(e) =>
                setFilters((prev) => ({
                  ...prev,
                  status: e.target.value,
                  page: 1,
                }))
              }
            >
              <option value="">
                {t("all")} {t("status")}
              </option>
              <option value="active">{t("active")}</option>
              <option value="inactive">{t("inactive")}</option>
              <option value="pending">{t("pending")}</option>
              {/* <option value="suspended">{t("suspended")}</option> */}
            </select>
          </div>
          <div className="d-flex flex-grow-1" style={{ minWidth: "200px" }}>
            <Select
              className="w-100"
              isClearable
              placeholder={`${t("all")} ${t("parents")}`}
              options={parentOptions.map((p) => ({
                value: p.id,
                label: p.full_name || p.email,
              }))}
              value={
                filters.parent_id
                  ? {
                      value: filters.parent_id,
                      label:
                        parentOptions.find((p) => p.id == filters.parent_id)
                          ?.full_name ||
                        parentOptions.find((p) => p.id == filters.parent_id)
                          ?.email ||
                        "",
                    }
                  : null
              }
              onChange={(selected) =>
                setFilters((prev) => ({
                  ...prev,
                  parent_id: selected ? selected.value : "",
                  page: 1,
                }))
              }
              styles={{
                control: (base) => ({
                  ...base,
                  minHeight: "38px",
                  fontSize: "0.875rem",
                }),
                menu: (base) => ({ ...base, zIndex: 9999 }),
              }}
            />
          </div>
          <div className="d-flex flex-grow-1">
            <select
              className="form-select"
              style={{ minWidth: "150px" }}
              value={filters.is_assigned}
              onChange={(e) =>
                setFilters((prev) => ({
                  ...prev,
                  is_assigned: e.target.value,
                  page: 1,
                }))
              }
            >
              <option value="">{t("all")}</option>
              <option value="true">{t("assigned")}</option>
              <option value="false">
                {t("not_assigned") || "Not Assigned"}
              </option>
            </select>
          </div>

          <div className="d-flex gap-2 ms-auto">
            <button
              className="btn btn-primary addDriver"
              onClick={handleResetFilters}
            >
              {t("btns.reset")}
            </button>

            {/* Export Buttons */}
            {/* {selectedStudents.length > 0 && ( */}
            <Tooltip
              title={`${t("export")} ${t("selected")} (${selectedStudents.length})`}
            >
              <button className="btn btn-success" onClick={exportToCSV}>
                <FaDownload className="me-1" />
                {t("export")} ({selectedStudents.length})
              </button>
            </Tooltip>
            {/* )} */}

            <Tooltip title={`${t("export")} ${t("all")} ${t("students")}`}>
              <button
                className="btn btn-info text-white"
                onClick={exportAllToCSV}
              >
                <FaDownload className="me-1" />
                {t("export")} {t("all")}
              </button>
            </Tooltip>
          </div>
        </div>
      </div>

      {/* ── Table ── */}
      <div className="table m-0">
        <table>
          <thead>
            <tr>
              <th className="text-center" style={{ width: "50px" }}>
                <input
                  type="checkbox"
                  checked={selectAll}
                  onChange={handleSelectAll}
                  disabled={students.length === 0}
                  className="form-check-input"
                />
              </th>

              <th>
                {t("Sr")}.{t("no")}.
              </th>
              <th className="text-nowrap">
                {t("student")} {t("name")}
              </th>
              <th className="text-nowrap">
                {t("roll")} {t("number")}
              </th>
              <th className="text-nowrap">
                {t("parent")} {t("name")}
              </th>
              <th className="text-nowrap">
                {t("parent")} {t("contact")}
              </th>
              <th
                className="text-nowrap"
                style={{ minWidth: "400px", width: "auto" }}
              >
                {t("address")}
              </th>
              <th>{t("status")}</th>
              <th>{t("disabled")}</th>
              <th className="text-nowrap">
                {t("reservation")} {t("status")}
              </th>
              <th>{t("created_at")}</th>
              <th>{t("action")}</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <SkeletonLoading MainLength={10} length={11} />
            ) : students.length > 0 ? (
              students.map((item, i) => (
                <tr key={item.id}>
                  <td className="text-center">
                    <input
                      type="checkbox"
                      checked={selectedStudents.includes(item.id)}
                      onChange={() => handleSelectStudent(item.id)}
                      disabled={!isSelectable(item)}
                      className="form-check-input"
                    />
                  </td>
                  <td>{(filters.page - 1) * filters.limit + i + 1}</td>
                  <td className="text-nowrap">{item?.full_name}</td>
                  <td className="text-nowrap">{item?.roll_number}</td>
                  <td className="text-nowrap">{item?.parent_name || "N/A"}</td>
                  <td className="text-nowrap">
                    <div>{item?.parent_email || "N/A"}</div>
                    <small className="text-muted">
                      {item?.parent_phone || ""}
                    </small>
                  </td>
                  <td style={{ minWidth: "400px" }}>{item?.street}</td>
                  <td className="text-nowrap">
                    {getStatusBadge(item?.status)}
                  </td>
                  <td className="text-nowrap">
                    {getDisabilityBadge(item?.is_disabled)}
                  </td>
                  <td className="text-nowrap">
                    {getAssignedBadge(item?.is_assigned_to_reservation)}
                  </td>
                  <td className="text-nowrap">
                    {item?.created_at
                      ? formatDateToLocal(item.created_at)
                      : "N/A"}
                  </td>
                  <td>
                    <ActionMenu
                      actions={[
                        {
                          label: `${t("view")} ${t("disability")} ${t("details")}`,
                          icon: <FaEye size={15} />,
                          onClick: () => {
                            setSelectedStudent(item);
                            setShowModal(true);
                          },
                        },
                      ]}
                    />
                  </td>
                </tr>
              ))
            ) : (
              <tr className="text-center">
                <td colSpan="11">
                  <span>
                    {t("no")} {t("student")} {t("found")}
                  </span>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <AdminPagination
        totalPages={pagination.totalPages}
        currentPage={pagination.page}
        limit={pagination.limit}
        onPageChange={handlePageChange}
        onLimitChange={handleLimitChange}
      />

      {/* ── Disability Details Modal ── */}
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton className="border-0">
          <Modal.Title className="h5">
            {t("disability")} {t("details")}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body
          style={{ maxHeight: "calc(80vh - 120px)", overflowY: "auto" }}
        >
          {selectedStudent && (
            <div className="p-2">
              {/* Student info header */}
              <div className="mb-3">
                <h6 className="fw-semibold mb-1">
                  {selectedStudent.full_name}
                </h6>
                <div className="d-flex gap-2 flex-wrap">
                  <small className="text-muted">
                    {t("roll")}: <strong>{selectedStudent.roll_number}</strong>
                  </small>
                  <small className="text-muted">
                    {t("parent")}:{" "}
                    <strong>{selectedStudent.parent_name}</strong>
                  </small>
                </div>
              </div>

              {/* Disability info */}
              <div className="mb-4">
                <h6 className="border-bottom pb-2 mb-3 fw-semibold">
                  {t("disability")} {t("information")}
                </h6>

                <div className="row mb-3 align-items-center">
                  <div className="col-4">
                    <strong className="text-muted">{t("status")}:</strong>
                  </div>
                  <div className="col-8">
                    {getDisabilityBadge(selectedStudent.is_disabled)}
                  </div>
                </div>

                {selectedStudent.disability_notes && (
                  <div className="row mb-3">
                    <div className="col-12">
                      <strong className="text-muted d-block mb-2">
                        {t("disability")} {t("notes")}:
                      </strong>
                      <div
                        className="p-3 bg-light rounded"
                        style={{
                          maxHeight: "200px",
                          overflowY: "auto",
                          fontSize: "0.9rem",
                        }}
                      >
                        {selectedStudent.disability_notes}
                      </div>
                    </div>
                  </div>
                )}

                {selectedStudent.dr_phone_no && (
                  <div className="row align-items-center">
                    <div className="col-4">
                      <strong className="text-muted">
                        {t("Doctor's")} {t("phone")}:
                      </strong>
                    </div>
                    <div className="col-8">
                      <a
                        href={`tel:${selectedStudent.dr_phone_no}`}
                        className="text-decoration-none"
                      >
                        <span className="badge bg-dark">
                          {selectedStudent.dr_phone_no}
                        </span>
                      </a>
                    </div>
                  </div>
                )}
              </div>

              {/* Student & reservation status */}
              <div className="pt-3 border-top">
                <div className="row mb-2">
                  <div className="col-4">
                    <strong className="text-muted">
                      {t("student")} {t("status")}:
                    </strong>
                  </div>
                  <div className="col-8">
                    {getStatusBadge(selectedStudent.status)}
                  </div>
                </div>

                <div className="row mb-2">
                  <div className="col-4">
                    <strong className="text-muted">
                      {t("reservation")} {t("status")}:
                    </strong>
                  </div>
                  <div className="col-8">
                    {getAssignedBadge(
                      selectedStudent.is_assigned_to_reservation,
                    )}
                  </div>
                </div>

                <div className="row">
                  <div className="col-4">
                    <strong className="text-muted">{t("address")}:</strong>
                  </div>
                  <div className="col-8">
                    <small className="text-muted">
                      {selectedStudent.street}
                    </small>
                  </div>
                </div>
              </div>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer className="border-0">
          <Button
            variant="secondary"
            onClick={() => setShowModal(false)}
            className="px-4"
          >
            {t("btns.close")}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default AllStudents;
